import dayjs from 'dayjs';

import { formatLocation } from 'FRONTEND/utils/backendApi/locationApi';
import { APPOINTMENT_TYPE_LIST, RESOURCE, EXPERT } from 'FRONTEND/utils/constants';
import { apiStatusError, fetchError } from 'FRONTEND/utils/errors';
import { hl7DateParams } from 'FRONTEND/utils/hl7';
import i18n from 'FRONTEND/utils/i18n';
import { API_AVAILABILITY_PATH, getApiAvailabilityPath, getApiAvailabilityRecurrencePath } from 'FRONTEND/utils/routes';

export const formatAvailability = (rawAvailability) => {
  const mainActor = rawAvailability?.actor?.[0]; // TODO: Is this reliable?
  const actorType = mainActor?.resourceType === 'Practitioner' ? EXPERT : RESOURCE;
  const location = rawAvailability?.actor?.find((actor) => actor.resourceType === 'Location' && actor.type === 'COMM');

  return {
    availabilityId: rawAvailability.id,
    startDate: rawAvailability.planningHorizon.start,
    endDate: rawAvailability.planningHorizon.end,
    title: i18n._('Availability'),
    typeList: rawAvailability.extension
      ?.find((ext) => ext.id === 'AppointmentTypes')
      ?.valueString.split(';') || [],
    tenantList: rawAvailability.extension
      ?.filter((ext) => ext.id !== 'AppointmentTypes')
      .map((ext) => ext.valueString),
    actorType,
    actorId: mainActor?.id,
    location: location && formatLocation(location),
  };
};

export const OVERLAP_ERROR = 'overlap';
export const BOOKED_ERROR = 'booked';
const ERROR_MAP = {
  'Specified dates overlap with existing availability.': OVERLAP_ERROR,
  'Cannot update availability because there are appointments associated to it.': BOOKED_ERROR,
};

// The HL7 Schedule model maps to Availability in our model. It makes more sense in the way we use those entities:
// it is a range of availability where appointments are allowed to be booked.
export async function getAvailabilities({ actorId, actorType, startDate, endDate }) {
  const queryParams = hl7DateParams({ startDate, endDate });
  if (actorType === RESOURCE) {
    queryParams.append('actor', `MOBL/${actorId}`);
  } else {
    queryParams.append('actor', `Practitioner/${actorId}`);
  }
  try {
    const response = await fetch(`${API_AVAILABILITY_PATH}?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    }).then((data) => {
      apiStatusError({ response: data });
      return data.json();
    });
    return response.entry.map((item) => formatAvailability(item.resource));
  } catch (error) {
    throw fetchError({ error });
  }
}

export async function createAvailability({ actorId, actorType, startDate, endDate, typeList = APPOINTMENT_TYPE_LIST, locationId, tenantList }) {
  const actor = actorType === 'resource' ? [
    { id: actorId, type: 'MOBL' },
    { id: locationId, type: 'COMM' },
  ] : [{ id: actorId, type: 'Practitioner' }];

  try {
    const entry = await fetch(API_AVAILABILITY_PATH, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Prefer: 'return=representation',
        'X-CSRF-Token': window.CSRFToken,
      },
      body: JSON.stringify({
        resourceType: 'Schedule',
        planningHorizon: {
          start: dayjs(startDate).toISOString(),
          end: dayjs(endDate).toISOString(),
        },
        actor,
        extension: [
          {
            id: 'AppointmentTypes',
            valueString: typeList.join(';'),
          },
          ...tenantList.map(tenantId => ({
            id: 'tenantId',
            valueString: tenantId,
          })),
        ],
      }),
    }).then((data) => {
      apiStatusError({ response: data });
      return data.json();
    });
    return formatAvailability(entry);
  } catch (error) {
    throw fetchError({ error });
  }
}

export async function updateAvailability({ availabilityId, actorId, locationId, actorType, startDate, endDate, typeList, tenantList }) {
  const actor = actorType === RESOURCE ? [
    { id: actorId, type: 'MOBL' },
    { id: locationId, type: 'COMM' },
  ] : [{ id: actorId, type: 'Practitioner' }];
  const body = {
    id: availabilityId,
    resourceType: 'Schedule',
    planningHorizon: {
      start: dayjs(startDate).toISOString(),
      end: dayjs(endDate).toISOString(),
    },
    actor,
  };
  if (typeList?.length) {
    body.extension = [
      {
        id: 'AppointmentTypes',
        valueString: typeList.join(';'),
      },
      ...tenantList.map(tenantId => ({
        id: 'tenantId',
        valueString: tenantId,
      })),
    ];
  }
  try {
    const entry = await fetch(getApiAvailabilityPath(availabilityId), {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        Prefer: 'return=representation',
        'X-CSRF-Token': window.CSRFToken,
      },
      body: JSON.stringify(body),
    }).then(async(data) => {
      if (!data.ok) {
        const result = await data.json();
        const message = ERROR_MAP[result.issue?.[0]?.details?.text];
        if (message) {
          throw new Error(message);
        }
      }
      apiStatusError({ response: data });
      return data.json();
    });
    return formatAvailability(entry.resource || entry);
  } catch (error) {
    throw fetchError({ error });
  }
}

export async function deleteAvailabilities({ availabilityId }) {
  try {
    await fetch(getApiAvailabilityPath(availabilityId), {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': window.CSRFToken,
      },
    }).then((data) => {
      apiStatusError({ response: data });
    });
  } catch (error) {
    throw fetchError({ error });
  }
}

export async function createAvailabilitiesRecurrence({ availabilityId, endDate }) {
  try {
    const entry = await fetch(getApiAvailabilityRecurrencePath(availabilityId), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': window.CSRFToken,
      },
      body: JSON.stringify({
        end: dayjs(endDate).format('YYYY-MM-DD'),
      }),
    }).then((data) => {
      apiStatusError({ response: data });
      return data.json();
    });
    if (entry.availabilities) {
      entry.availabilities = entry.availabilities.map((rawAvailability) => formatAvailability(rawAvailability.resource));
    }
    return entry;
  } catch (error) {
    throw fetchError({ error });
  }
}
